import React, { useState } from "react";
import { ChevronRight, Camera, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PhotosStep = ({ onNext }) => {
  const [skipPhotos, setSkipPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await api.submitPhotos({
        photos: [],
        skip_for_now: true
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Add Photos</h2>
      <p className="text-gray-600 mb-6">Photos help others see the real you</p>

      {/* Info Card */}
      <div className="bg-[#1C3A7A]/5 border border-[#1C3A7A]/20 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#1C3A7A]/10 rounded-full flex items-center justify-center flex-shrink-0">
            <Camera className="w-5 h-5 text-[#1C3A7A]" />
          </div>
          <div>
            <p className="font-medium text-[#1C3A7A] mb-1">Photos don&apos;t affect matching</p>
            <p className="text-sm text-gray-600">
              We match based on values and compatibility, not looks. Photos help start conversations, but they won&apos;t change who you&apos;re matched with.
            </p>
          </div>
        </div>
      </div>

      {/* Photo Upload Placeholder */}
      <div className="mb-6">
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-[3/4] bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#1C3A7A] transition-colors"
            >
              <Camera className="w-8 h-8 text-gray-400" />
            </div>
          ))}
        </div>
        <p className="text-gray-500 text-sm text-center">
          Photo upload coming soon. You can add photos later from your profile.
        </p>
      </div>

      {/* Skip Option */}
      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900 mb-1">Skip for now</p>
            <p className="text-sm text-gray-600">
              You can add photos anytime from your profile settings. Your matches won&apos;t be affected.
            </p>
          </div>
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue Without Photos
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default PhotosStep;
